/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { Image } from "@packages/framework";
import { constantSemanticColors, radius, spacing } from "@packages/ui";
import QRCode from "react-qr-code";

const QR_SIZE = 200;
const LOGO_SIZE = 56;

const containerStyle: Interpolation = {
  alignSelf: "center",
  backgroundColor: constantSemanticColors.onColorHighest,
  borderRadius: radius.l,
  padding: spacing.l,
  position: "relative",
};
const logoWrapperStyle: Interpolation = {
  alignItems: "center",
  backgroundColor: constantSemanticColors.onColorHighest,
  borderRadius: radius.full,
  display: "flex",
  justifyContent: "center",
  left: "50%",
  padding: spacing.xs,
  position: "absolute",
  top: "50%",
  transform: "translate(-50%, -50%)",
};
const logoStyle: Interpolation = {
  borderRadius: radius.full,
  height: LOGO_SIZE,
  objectFit: "cover",
  width: LOGO_SIZE,
};

export type CommunityInviteQrCodeProps = {
  inviteUrl: string;
  imageUrl?: string | null;
  imageAlt?: string;
};

/**
 * QR code encoding the community invite URL with the community's profile image
 * composited in the centre. Uses error-correction level "H" so the QR still
 * scans with the centre logo occluding part of it.
 *
 * The centre logo is a Hallow CDN image that is NOT served with CORS headers,
 * so it is loaded through `next/image` (the framework `Image`). That routes it
 * through the same-origin `/_next/image` optimizer, which (a) renders on screen
 * with no `crossOrigin` tainting — a plain `<img crossOrigin>` on the raw CDN
 * URL gets refused by the browser because the CDN sends no `Access-Control-
 * Allow-Origin` — and (b) is same-origin, so `html-to-image` can read it into
 * the exported PNG without tainting the canvas.
 */
export const CommunityInviteQrCode = ({
  inviteUrl,
  imageUrl,
  imageAlt,
}: CommunityInviteQrCodeProps) => (
  <div css={containerStyle}>
    <QRCode
      value={inviteUrl}
      level="H"
      size={QR_SIZE}
      style={{ display: "block", height: QR_SIZE, width: QR_SIZE }}
    />
    {imageUrl && (
      <div css={logoWrapperStyle}>
        <Image
          alt={imageAlt ?? ""}
          css={logoStyle}
          height={LOGO_SIZE}
          src={imageUrl}
          width={LOGO_SIZE}
        />
      </div>
    )}
  </div>
);

CommunityInviteQrCode.displayName = "CommunityInviteQrCode";
